import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../i18n/I18nContext';
import { usePermissions } from '../hooks/usePermissions';
import { api } from '../utils/api';
import { TRANSLATIONS } from '../i18n/translations';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft, Download, TrendingUp, Users, CheckCircle, Clock, 
  Store, ChevronRight, X, Trash2, FileText, MessageSquare 
} from 'lucide-react';
import { toast } from 'sonner';

export default function Analytics() {
  const { t, lang } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { canExport, canReset } = usePermissions();
  const [course, setCourse] = useState(null);
  const [stats, setStats] = useState(null);
  const [storeStats, setStoreStats] = useState(null);
  const [feedbackData, setFeedbackData] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Store detail drawer state
  const [selectedStore, setSelectedStore] = useState(null);
  const [participants, setParticipants] = useState(null);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  
  // Reset data dialog state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  
  // Mystery shop overview state
  const [msOverview, setMsOverview] = useState(null);

  const isMysteryShop = course?.experience_type === 'mystery_shop';

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [courseRes, statsRes] = await Promise.all([
        api.get(`/courses/${id}`),
        api.get(`/analytics/courses/${id}`)
      ]);
      setCourse(courseRes.data);
      setStats(statsRes.data);
      
      // Fetch client info if course has client_id
      if (courseRes.data.client_id) {
        try {
          const clientRes = await api.get(`/clients/${courseRes.data.client_id}`);
          setClientInfo(clientRes.data);
        } catch (e) {
          console.log('Could not fetch client info:', e);
        }
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreStats = async () => {
    try {
      const response = await api.get(`/analytics/courses/${id}/by_store`);
      setStoreStats(response.data);
    } catch (error) {
      console.error('Failed to fetch store stats:', error);
      toast.error('Failed to load store analytics');
    }
  };

  const fetchMsOverview = async () => {
    try {
      const response = await api.get(`/analytics/courses/${id}/mystery_shop/overview`);
      setMsOverview(response.data);
    } catch (error) {
      console.error('Failed to fetch mystery shop overview:', error);
      toast.error('Failed to load mystery shop overview');
    }
  };

  const downloadStorePdf = async (storeId, storeName) => {
    try {
      const response = await api.get(`/analytics/courses/${id}/stores/${storeId}/report/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (storeName || storeId).replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 30);
      link.setAttribute('download', `mystery_shop_${safeName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to generate PDF report');
    }
  };

  const fetchParticipants = async (storeId, storeName) => {
    setSelectedStore({ id: storeId, name: storeName });
    setLoadingParticipants(true);
    try {
      const response = await api.get(`/analytics/courses/${id}/stores/${storeId}/participants`);
      setParticipants(response.data);
    } catch (error) {
      console.error('Failed to fetch participants:', error);
      toast.error('Failed to load participants');
    } finally {
      setLoadingParticipants(false);
    }
  };

  const closeParticipantsDrawer = () => {
    setSelectedStore(null);
    setParticipants(null);
  };

  // Reset all test data for this course
  const handleResetData = async () => {
    setIsResetting(true);
    try {
      await api.post(`/courses/${id}/reset-data`);
      toast.success(t('analytics_reset_success'));
      setResetDialogOpen(false);
      // Reset local state and refetch data
      setStats(null);
      setStoreStats(null);
      setFeedbackData(null);
      setLoading(true);
      fetchData();
    } catch (error) {
      console.error('Failed to reset data:', error);
      toast.error(t('analytics_reset_error'));
    } finally {
      setIsResetting(false);
    }
  };

  // Reset data for a specific store only
  const [storeResetDialogOpen, setStoreResetDialogOpen] = useState(false);
  const [resettingStoreId, setResettingStoreId] = useState(null);
  const [isResettingStore, setIsResettingStore] = useState(false);

  const handleResetStoreData = async (storeId) => {
    setIsResettingStore(true);
    try {
      await api.delete(`/analytics/courses/${id}/stores/${storeId}/reset`);
      toast.success(t('analytics_reset_store_success'));
      setStoreResetDialogOpen(false);
      setResettingStoreId(null);
      // Refetch store stats and close drawer if open
      fetchStoreStats();
      if (selectedStore?.id === storeId) {
        closeParticipantsDrawer();
      }
      // Also refetch overview stats
      fetchData();
    } catch (error) {
      console.error('Failed to reset store data:', error);
      toast.error(t('analytics_reset_store_error'));
    } finally {
      setIsResettingStore(false);
    }
  };

  // Fetch feedback data
  const fetchFeedback = async () => {
    try {
      const response = await api.get(`/courses/${id}/feedback`);
      setFeedbackData(response.data);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
      toast.error(lang === 'nl' ? 'Kon feedback niet laden' : 'Failed to load feedback');
    }
  };

  // Tab change handler
  const handleTabChange = (value) => {
    setActiveTab(value);
    if (value === 'by_store') {
      if (!storeStats) fetchStoreStats();
      if (isMysteryShop && !msOverview) fetchMsOverview();
    }
    if (value === 'feedback' && !feedbackData) {
      fetchFeedback();
    }
  };

  // Export functions with bilingual CSV headers
  const exportCSV = async () => {
    try {
      const response = await api.get(`/analytics/courses/${id}/export`, { responseType: 'blob' });
      downloadBlob(response.data, `analytics_${id}.csv`);
      toast.success(lang === 'nl' ? 'Analytics geëxporteerd!' : 'Analytics exported!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(lang === 'nl' ? 'Export mislukt' : 'Failed to export analytics');
    }
  };

  const exportStoreCSV = async () => {
    if (!storeStats?.stores?.length) return;
    
    // Get translated headers
    const translations = TRANSLATIONS[lang];
    const headers = [
      translations.analytics_csv_store_id,
      translations.analytics_csv_store_name,
      translations.analytics_csv_views,
      translations.analytics_csv_started,
      translations.analytics_csv_completed,
      translations.analytics_csv_completion_rate,
      ...(isMysteryShop ? [] : [translations.analytics_csv_avg_score]),
      translations.analytics_csv_unique_learners
    ];
    
    // Build CSV with translated headers
    const rows = [headers.join(',')];
    storeStats.stores.forEach(store => {
      rows.push([
        store.store_id,
        `"${store.store_name}"`,
        store.views,
        store.started,
        store.completed,
        `${store.completion_rate}%`,
        ...(isMysteryShop ? [] : [`${store.avg_score}%`]),
        store.unique_learners
      ].join(','));
    });
    
    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `analytics_by_store_${id}.csv`);
    toast.success(lang === 'nl' ? 'Winkel analytics geëxporteerd!' : 'Store analytics exported!');
  };

  const exportParticipantsCSV = async () => {
    if (!selectedStore || !participants?.participants?.length) return;
    
    // Get translated headers
    const translations = TRANSLATIONS[lang];
    const headers = [
      translations.analytics_csv_learner_name,
      translations.analytics_csv_status,
      translations.analytics_csv_attempts,
      translations.analytics_csv_total_questions,
      translations.analytics_csv_correct_answers,
      translations.analytics_csv_score_percent,
      translations.analytics_csv_time_spent,
      translations.analytics_csv_first_activity,
      translations.analytics_csv_last_activity
    ];
    
    // Build CSV with translated headers
    const rows = [headers.join(',')];
    participants.participants.forEach(p => {
      // Translate status
      const statusTranslations = {
        completed: translations.analytics_status_completed,
        started: translations.analytics_status_started,
        not_started: translations.analytics_status_not_started
      };
      rows.push([
        `"${p.learner_name}"`,
        statusTranslations[p.status] || p.status,
        p.attempts,
        p.total_questions,
        p.correct_answers,
        `${p.score_percent}%`,
        p.time_spent_seconds,
        p.first_activity,
        p.last_activity
      ].join(','));
    });
    
    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `participants_${selectedStore.name.replace(/\s+/g, '_')}.csv`);
    toast.success(lang === 'nl' ? 'Deelnemers geëxporteerd!' : 'Participants exported!');
  };

  // Export feedback as CSV
  const exportFeedbackCSV = async () => {
    try {
      const response = await api.get(`/courses/${id}/feedback/export?lang=${lang}`, { responseType: 'blob' });
      downloadBlob(response.data, `feedback_${id}.csv`);
      toast.success(lang === 'nl' ? 'Feedback geëxporteerd!' : 'Feedback exported!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(lang === 'nl' ? 'Export mislukt' : 'Failed to export feedback');
    }
  };

  // Export comprehensive data as Excel (.xlsx)
  const exportExcel = async () => {
    try {
      const response = await api.get(`/analytics/courses/${id}/export/xlsx?lang=${lang}`, { responseType: 'blob' });
      downloadBlob(response.data, `participants_${id}.xlsx`);
      toast.success(lang === 'nl' ? 'Excel geëxporteerd!' : 'Excel exported!');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error(lang === 'nl' ? 'Export mislukt' : 'Failed to export Excel');
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Format time helper
  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  // Status badge helper
  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-800',
      started: 'bg-yellow-100 text-yellow-800',
      not_started: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      completed: t('analytics_status_completed') || 'Completed',
      started: t('analytics_status_started') || 'Started',
      not_started: t('analytics_status_not_started') || 'Not Started'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.not_started}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{t('loading')}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="analytics">
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">{t('analytics_title')}</h1>
              <p className="text-sm text-gray-600">{course?.title}</p>
              {clientInfo && (
                <p className="text-xs text-gray-500 mt-1">
                  {t('analytics_client_label')}: {clientInfo.name}
                </p>
              )}
            </div>
          </div>
          {canReset && (
            <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  data-testid="reset-data-button"
                  title={t('analytics_reset_tooltip')}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('analytics_reset_button')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('analytics_reset_dialog_title')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('analytics_reset_dialog_message')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isResetting}>
                    {t('analytics_reset_cancel')}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleResetData}
                    disabled={isResetting}
                    className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                    data-testid="reset-data-confirm-button"
                  >
                    {isResetting ? t('loading') : t('analytics_reset_confirm')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="overview">{t('analytics_tab_overview')}</TabsTrigger>
            <TabsTrigger value="by_store">{t('analytics_tab_by_store')}</TabsTrigger>
            <TabsTrigger value="feedback">{t('analytics_tab_feedback')}</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {canExport && (
              <div className="flex justify-end gap-2 mb-4">
                <Button variant="outline" onClick={exportCSV} data-testid="export-csv-button">
                  <Download className="h-4 w-4 mr-2" />
                  {t('analytics_export')}
                </Button>
                <Button variant="outline" onClick={exportExcel} data-testid="export-excel-button">
                  <Download className="h-4 w-4 mr-2" />
                  {t('analytics_export_excel')}
                </Button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card data-testid="stat-views">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{t('analytics_views')}</p>
                      <p className="text-3xl font-bold mt-1">{stats?.views || 0}</p>
                    </div>
                    <Users className="h-8 w-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="stat-starts">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{t('analytics_starts')}</p>
                      <p className="text-3xl font-bold mt-1">{stats?.starts || 0}</p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="stat-completions">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{t('analytics_completions')}</p>
                      <p className="text-3xl font-bold mt-1">{stats?.completions || 0}</p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="stat-avg-time">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{t('analytics_avg_time')}</p>
                      <p className="text-3xl font-bold mt-1">{formatTime(stats?.avg_time_seconds)}</p>
                    </div>
                    <Clock className="h-8 w-8 text-orange-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('analytics_completion_rate')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <p className="text-5xl font-bold" style={{ color: '#667eea' }} data-testid="completion-rate">
                      {stats?.completion_rate?.toFixed(1) || 0}%
                    </p>
                    <p className="text-gray-600 mt-2">{t('analytics_of_learners')}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{isMysteryShop ? t('analytics_responses') : t('analytics_quiz_accuracy')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    {isMysteryShop ? (
                      <>
                        <p className="text-5xl font-bold text-blue-600" data-testid="quiz-accuracy">
                          {stats?.total_quiz_submissions || stats?.starts || 0}
                        </p>
                        <p className="text-gray-600 mt-2">{t('analytics_responses_submitted')}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-5xl font-bold" style={{ color: '#10b981' }} data-testid="quiz-accuracy">
                          {stats?.quiz_accuracy?.toFixed(1) || 0}%
                        </p>
                        <p className="text-gray-600 mt-2">{t('analytics_avg_correct')}</p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* By Store Tab */}
          <TabsContent value="by_store">
            {isMysteryShop ? (
              /* Mystery Shop: store-level overview with answer distribution + PDF */
              <>
                {canExport && (
                  <div className="flex justify-end mb-4 gap-2">
                    <Button variant="outline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/courses/${id}/export/xlsx?lang=${lang}`, '_blank')} data-testid="export-xlsx-button">
                      <Download className="h-4 w-4 mr-2" />
                      Export XLSX
                    </Button>
                  </div>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Store className="h-5 w-5" />
                      Store Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!msOverview ? (
                      <div className="text-center py-8 text-gray-500">{t('loading')}</div>
                    ) : msOverview.stores?.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        {t('analytics_no_store_data')}
                      </div>
                    ) : (
                      <div className="space-y-3" data-testid="ms-store-overview">
                        {msOverview.stores.map((store) => {
                          const totalAnswers = store.answer_distribution?.reduce((sum, d) => sum + d.count, 0) || 0;
                          return (
                            <div 
                              key={store.store_id} 
                              className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                              data-testid={`ms-store-row-${store.store_id}`}
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => fetchParticipants(store.store_id, store.store_name)}>
                                  <h3 className="font-semibold text-gray-900 text-sm">{store.store_name}</h3>
                                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                    <span className="flex items-center gap-1">
                                      <Users className="h-3 w-3" />
                                      {store.visit_count} {store.visit_count === 1 ? 'visit' : 'visits'}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <MessageSquare className="h-3 w-3" />
                                      {store.comment_count} {store.comment_count === 1 ? 'comment' : 'comments'}
                                    </span>
                                    {store.latest_visit && (
                                      <span className="flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        {new Date(store.latest_visit).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                                  {canExport && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-8 px-2 text-xs"
                                      onClick={(e) => { e.stopPropagation(); downloadStorePdf(store.store_id, store.store_name); }}
                                      data-testid={`pdf-download-${store.store_id}`}
                                    >
                                      <FileText className="h-3.5 w-3.5 mr-1" />
                                      PDF
                                    </Button>
                                  )}
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="h-8 px-2"
                                    onClick={() => fetchParticipants(store.store_id, store.store_name)}
                                  >
                                    <ChevronRight className="h-4 w-4 text-gray-400" />
                                  </Button>
                                </div>
                              </div>
                              
                              {/* Answer distribution bar */}
                              {totalAnswers > 0 && (
                                <div>
                                  <div className="flex h-3 rounded-full overflow-hidden" data-testid={`answer-bar-${store.store_id}`}>
                                    {store.answer_distribution.map((d, i) => (
                                      <div 
                                        key={i} 
                                        style={{ 
                                          width: `${(d.count / totalAnswers) * 100}%`, 
                                          backgroundColor: d.color,
                                          minWidth: d.count > 0 ? '4px' : '0'
                                        }} 
                                        title={`${d.label}: ${d.count}`}
                                      />
                                    ))}
                                  </div>
                                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                                    {store.answer_distribution.map((d, i) => (
                                      <span key={i} className="flex items-center gap-1 text-xs text-gray-500">
                                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                                        {d.label} ({d.count})
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              /* Training/Guide: existing by-store table */
              <>
                {canExport && (
                  <div className="flex justify-end mb-4">
                    <Button variant="outline" onClick={exportStoreCSV} data-testid="export-store-csv-button">
                      <Download className="h-4 w-4 mr-2" />
                      {t('analytics_export_stores')}
                    </Button>
                  </div>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Store className="h-5 w-5" />
                      {t('analytics_stores_title')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!storeStats ? (
                      <div className="text-center py-8 text-gray-500">{t('loading')}</div>
                    ) : storeStats.stores?.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        {t('analytics_no_store_data')}
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-3 px-2 font-medium">{t('analytics_store')}</th>
                              <th className="text-right py-3 px-2 font-medium">{t('analytics_learners')}</th>
                              <th className="text-right py-3 px-2 font-medium">{t('analytics_started')}</th>
                              <th className="text-right py-3 px-2 font-medium">{t('analytics_completed')}</th>
                              <th className="text-right py-3 px-2 font-medium">{t('analytics_completion_rate')}</th>
                              <th className="text-right py-3 px-2 font-medium">{t('analytics_avg_score')}</th>
                              <th className="text-right py-3 px-2 font-medium"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {storeStats.stores?.map((store) => (
                              <tr 
                                key={store.store_id} 
                                className="border-b hover:bg-gray-50 cursor-pointer"
                                onClick={() => fetchParticipants(store.store_id, store.store_name)}
                              >
                                <td className="py-3 px-2 font-medium">{store.store_name}</td>
                                <td className="text-right py-3 px-2">{store.unique_learners}</td>
                                <td className="text-right py-3 px-2">{store.started}</td>
                                <td className="text-right py-3 px-2">{store.completed}</td>
                                <td className="text-right py-3 px-2">
                                  <span className={store.completion_rate >= 70 ? 'text-green-600' : store.completion_rate >= 40 ? 'text-yellow-600' : 'text-red-600'}>
                                    {store.completion_rate}%
                                  </span>
                                </td>
                                <td className="text-right py-3 px-2">
                                  <span className={store.avg_score >= 70 ? 'text-green-600' : store.avg_score >= 40 ? 'text-yellow-600' : 'text-red-600'}>
                                    {store.avg_score}%
                                  </span>
                                </td>
                                <td className="text-right py-3 px-2">
                                  <ChevronRight className="h-4 w-4 text-gray-400" />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  {t('analytics_feedback_title')}
                  {feedbackData && (
                    <span className="text-sm font-normal text-gray-500">
                      ({feedbackData.total || 0})
                    </span>
                  )}
                </CardTitle>
                <Button variant="outline" onClick={exportFeedbackCSV} disabled={!feedbackData?.feedback?.length} data-testid="export-feedback-button" style={{ display: canExport ? undefined : 'none' }}>
                  <Download className="h-4 w-4 mr-2" />
                  {t('analytics_export_feedback')}
                </Button>
              </CardHeader>
              <CardContent>
                {!feedbackData ? (
                  <div className="text-center py-8 text-gray-500">{t('loading')}</div>
                ) : feedbackData.feedback?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {t('analytics_no_feedback')}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="feedback-table">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2 font-medium">{t('analytics_feedback_date')}</th>
                          <th className="text-left py-3 px-2 font-medium">{t('analytics_feedback_time')}</th>
                          <th className="text-left py-3 px-2 font-medium">{t('analytics_feedback_store')}</th>
                          <th className="text-left py-3 px-2 font-medium">{t('analytics_feedback_name')}</th>
                          <th className="text-left py-3 px-2 font-medium">{t('analytics_feedback_text')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feedbackData.feedback.map((fb, idx) => {
                          // Parse and format date/time based on language
                          let dateStr = '';
                          let timeStr = '';
                          try {
                            const dt = new Date(fb.created_at);
                            dateStr = dt.toLocaleDateString(lang === 'nl' ? 'nl-NL' : 'en-GB', { 
                              day: 'numeric', 
                              month: 'short', 
                              year: 'numeric' 
                            });
                            timeStr = dt.toLocaleTimeString(lang === 'nl' ? 'nl-NL' : 'en-GB', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            });
                          } catch (e) {
                            dateStr = fb.created_at?.substring(0, 10) || '';
                            timeStr = fb.created_at?.substring(11, 16) || '';
                          }
                          
                          return (
                            <tr key={fb.id || idx} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-2 text-sm">{dateStr}</td>
                              <td className="py-3 px-2 text-sm">{timeStr}</td>
                              <td className="py-3 px-2 text-sm">{fb.store_name || '-'}</td>
                              <td className="py-3 px-2 text-sm font-medium">{fb.learner_name || '-'}</td>
                              <td className="py-3 px-2 text-sm max-w-md">
                                <p className="truncate" title={fb.feedback_text}>
                                  {fb.feedback_text}
                                </p>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Participants Drawer/Modal */}
      {selectedStore && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={closeParticipantsDrawer}>
          <div 
            className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold">{selectedStore.name}</h2>
                <p className="text-sm text-gray-600">
                  {participants?.total_participants || 0} {t('analytics_participants')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {canExport && (
                  <Button variant="outline" size="sm" onClick={exportParticipantsCSV} data-testid="export-participants-button">
                    <Download className="h-4 w-4 mr-1" />
                    {t('analytics_export_participants')}
                  </Button>
                )}
                {canReset && (
                  <AlertDialog open={storeResetDialogOpen && resettingStoreId === selectedStore.id} onOpenChange={(open) => {
                    setStoreResetDialogOpen(open);
                    if (!open) setResettingStoreId(null);
                  }}>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => setResettingStoreId(selectedStore.id)}
                        data-testid="reset-store-data-button"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        {t('analytics_reset_store_button')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('analytics_reset_store_dialog_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('analytics_reset_store_dialog_message', { storeName: selectedStore.name })}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('analytics_reset_cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleResetStoreData(selectedStore.id)}
                          className="bg-red-600 hover:bg-red-700"
                          data-testid="reset-store-data-confirm-button"
                        >
                          {isResettingStore ? t('loading') : t('analytics_reset_store_confirm')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                <Button variant="ghost" size="sm" onClick={closeParticipantsDrawer}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <div className="p-6">
              {loadingParticipants ? (
                <div className="text-center py-8 text-gray-500">{t('loading')}</div>
              ) : participants?.participants?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {t('analytics_no_participants')}
                </div>
              ) : (
                <div className="space-y-4">
                  {participants?.participants?.map((p, idx) => (
                    <Card key={idx} className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-medium">{p.learner_name}</h3>
                          <p className="text-xs text-gray-500">
                            {t('analytics_last_activity')}: {new Date(p.last_activity).toLocaleDateString(lang === 'nl' ? 'nl-NL' : 'en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        {getStatusBadge(p.status)}
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-sm">
                        <div>
                          <p className="text-gray-500 text-xs">{t('analytics_attempts')}</p>
                          <p className="font-medium">{p.attempts}</p>
                        </div>
                        <div>
                          {isMysteryShop ? (
                            <>
                              <p className="text-gray-500 text-xs">{t('analytics_questions_answered')}</p>
                              <p className="font-medium">{p.total_questions}</p>
                            </>
                          ) : (
                            <>
                              <p className="text-gray-500 text-xs">{t('analytics_quiz_score')}</p>
                              <p className="font-medium">{p.correct_answers}/{p.total_questions} ({p.score_percent}%)</p>
                            </>
                          )}
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">{t('analytics_time_spent')}</p>
                          <p className="font-medium">{formatTime(p.time_spent_seconds)}</p>
                        </div>
                      </div>
                      {/* Per-question answer overview for mystery_shop */}
                      {isMysteryShop && p.quiz_answers && p.quiz_answers.length > 0 && (
                        <div className="mt-3 space-y-2" data-testid={`answer-overview-${idx}`}>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('analytics_answer_overview')}</p>
                          {p.quiz_answers.map((qa, qi) => (
                            <div key={qi} className="bg-gray-50 rounded-md p-3 text-sm">
                              <p className="font-medium text-gray-800 mb-1">{qa.question}</p>
                              <p className="text-gray-600">
                                <span className="text-gray-400 text-xs mr-1">{t('analytics_answer_label')}</span>
                                {Array.isArray(qa.selected) ? qa.selected.join(', ') : String(qa.selected || '')}
                              </p>
                              {qa.comment && (
                                <p className="text-gray-600 mt-1">
                                  <span className="text-gray-400 text-xs mr-1">{t('analytics_comment_label')}</span>
                                  {qa.comment}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
